import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { CamhsChild, DataShareConsent, getPartnerInfo } from '../../api/report/report';
import { getCognitoId } from '../../utilities/amplify';

export interface OrgUnit {
    id?: number;
    fullId?: string;
    fullName?: string;
    unitName?: string;
}

export interface UserState {
    cognitoId: string;
    externalId: string;
    firstName: string;
    lastName: string;
    title: string;
    profilePicture?: string | null;
    partnerType: string;
    ownOrgUnit: OrgUnit;
    descendantOrgUnits: OrgUnit[];
    showConnectionsFeature?: boolean;
    reportRecipient?: 'ALWAYS' | 'EXPERT_DECISION';
    partnerCamhsChildren?: CamhsChild[] | null;
    partnerDataShareConsent?: DataShareConsent[] | null;
}

export const initialState: UserState = {
    cognitoId: '',
    externalId: '',
    firstName: '',
    lastName: '',
    profilePicture: '',
    title: '',
    partnerType: '',
    ownOrgUnit: {
        id: undefined,
        fullId: '',
        fullName: '',
        unitName: '',
    },
    descendantOrgUnits: [],
};

export const asyncPopulateUser = createAsyncThunk('user/populate', async (_, { dispatch }) => {
    // getCognitoId (Cognito Identity credentials) and getPartnerInfo (partners-read) are
    // independent reads — getPartnerInfo resolves its own SigV4 credentials via Amplify and does
    // not need the returned cognitoId value — so run them concurrently instead of serially. This
    // shaves a Cognito Identity round-trip off the login critical path, which gates the redirect
    // to /dashboard. The thunk still rejects if either read throws (surfaced via .unwrap() at the
    // call site) rather than silently leaving the user unpopulated.
    const [cognitoId, partner] = await Promise.all([getCognitoId(), getPartnerInfo()]);
    dispatch(populate({ ...partner, cognitoId }));
});

export const userSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {
        populate: (state, action: PayloadAction<UserState>) => {
            Object.assign(state, action.payload);
        },
        logout: () => initialState,
    },
});

export const { populate, logout } = userSlice.actions;

export default userSlice.reducer;
